import type { TranslationParams } from '@/context/LanguageContext';
import type { AdaptcutTool, WearTrace } from '../type/WearTypes';

type Translate = (key: string, params?: TranslationParams) => string;

/** Number with a fixed count of decimals, in the UI language. */
export function formatNumber(language: string, value: number, digits: number): string {
  return value.toLocaleString(language, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Wear as a share of the predicted life: "68 %", "< 1 %", "> 99 %" just below 1. */
export function formatWear(language: string, wear: number): string {
  if (wear > 0 && wear < 0.005) return `< ${formatNumber(language, 1, 0)} %`;
  if (wear >= 0.995 && wear < 1) return `> ${formatNumber(language, 99, 0)} %`;
  return `${formatNumber(language, wear * 100, 0)} %`;
}

/** Probability: one decimal under 10 %, bounded display near 0 and 1. */
export function formatProbability(language: string, p: number): string {
  if (p < 0.001) return `< ${formatNumber(language, 0.1, 1)} %`;
  if (p > 0.999) return `> ${formatNumber(language, 99.9, 1)} %`;
  return `${formatNumber(language, p * 100, p < 0.1 ? 1 : 0)} %`;
}

/** Dictionary entries with `one` / `other` variants (the dictionaries have no plural rules). */
export function plural(
  t: Translate,
  key: string,
  count: number,
  params: TranslationParams = {}
): string {
  return t(`${key}.${count === 1 ? 'one' : 'other'}`, { count, ...params });
}

/** "Ø coupe 2,00 mm · 3 dents · Ø queue 4 mm" for an Adaptcut tool. */
export function adaptcutToolLine(tool: AdaptcutTool, language: string, t: Translate): string {
  const mm = (value: number | null, digits: number) =>
    value === null ? '—' : `${formatNumber(language, value, digits)} mm`;
  return t('mlWear.context.adaptcutLine', {
    cut: mm(tool.diameterCut, 2),
    teeth: tool.teeth ?? '—',
    shank: mm(tool.diameter, 0),
  });
}

/** Colour band of a wear value: past the prudent threshold = warn, past the predicted life = bad. */
export function wearLevel(wear: number, prudentWear: number): 'good' | 'warn' | 'bad' {
  if (wear >= 1) return 'bad';
  if (wear >= prudentWear) return 'warn';
  return 'good';
}

/** The fields the wear model needs that a trace left out of the wear lacks (volume, ae, fz). */
export function missingFields(trace: WearTrace): Array<'volume' | 'ae' | 'fz'> {
  if (trace.skippedReason === null) return [];
  const fields: Array<'volume' | 'ae' | 'fz'> = [];
  if (trace.volume === null) fields.push('volume');
  if (trace.ae === null) fields.push('ae');
  if (trace.fz === null) fields.push('fz');
  return fields;
}

/** "ae and fz" / "ae et fz" / "ae und fz" in the UI language. */
export function listFields(language: string, fields: string[]): string {
  try {
    return new Intl.ListFormat(language, { type: 'conjunction' }).format(fields);
  } catch {
    return fields.join(', ');
  }
}
